import type { NotificationPrefs } from '../types';

export type NotificationAudience = 'internal' | 'portal';

export type DeliveryBadgeKind = 'bell' | 'email' | 'dm' | 'crm' | 'off';

export interface DeliveryBadge {
  kind: DeliveryBadgeKind;
  label: string;
}

/** Task/ops alert toggles in notification settings. */
export type TaskEventPrefKey =
  | 'onTaskAssigned'
  | 'onTaskStatusChanged'
  | 'onActionRequired'
  | 'onComment'
  | 'onMention'
  | 'onTaskCompleted';

export type CardExpenseDigestPrefKey = 'onCardExpenseDigest';

export interface NotificationEventMeta {
  key: TaskEventPrefKey;
  label: string;
  hint: string;
}

export const NOTIFICATION_EVENT_OPTIONS: NotificationEventMeta[] = [
  {
    key: 'onTaskAssigned',
    label: 'Task assigned to me',
    hint: 'When you are added to a project or Operations task.',
  },
  {
    key: 'onTaskStatusChanged',
    label: 'Task status changed',
    hint: 'When a task you are on moves to a new status.',
  },
  {
    key: 'onActionRequired',
    label: 'Action required',
    hint: 'When someone sets you as the person who must act.',
  },
  {
    key: 'onComment',
    label: 'Comments on my tasks',
    hint: 'When someone comments on a task you are assigned to, or on an inspiration board you follow.',
  },
  {
    key: 'onMention',
    label: '@Mentions',
    hint: 'When someone @mentions you in a task, Operations, or inspiration-board comment.',
  },
  {
    key: 'onTaskCompleted',
    label: 'Task completed',
    hint: 'When a task you are on is marked complete.',
  },
];

export const CARD_EXPENSE_DIGEST_META = {
  key: 'onCardExpenseDigest' as const,
  label: 'Card expense digest',
  hint: 'Weekday reminder when company card charges need categorization in My Card Expenses.',
};

/** Badges for card expense digest — in-app bell only (Slack DMs are off). */
export function getCardExpenseDigestBadges(_prefs: NotificationPrefs, enabled: boolean): DeliveryBadge[] {
  if (!enabled) return [{ kind: 'off', label: 'Off' }];
  return [{ kind: 'bell', label: 'Bell' }];
}

/** Where each alert can land when its toggle is on (given channel prefs above). */
const EVENT_DELIVERY: Record<TaskEventPrefKey, { bell?: boolean; email?: boolean }> = {
  onTaskAssigned: { bell: true, email: true },
  onTaskStatusChanged: { email: true },
  onActionRequired: { email: true },
  onComment: { bell: true, email: true },
  onMention: { bell: true, email: true },
  onTaskCompleted: { email: true },
};

export const AUTOMATIC_IN_APP = [
  'PDF comments and @mentions on drawings',
  'Inspiration board comments and @mentions',
  'Leads, deals, portal activity, RFQ, and billing alerts',
  'OPS proposal review when you are the selected reviewer',
];

export const TEAM_SLACK_NOTE =
  'The #CRM Slack channel only posts milestones (signed, paid, RFQ, quote approved, OPS review, new lead). Day-to-day comments and tasks stay in the CRM bell. That does not change based on anyone’s settings.';

export const CHANNEL_COPY = {
  personalScope:
    'These settings apply to you only. They do not change notifications for other people or what gets posted to #CRM.',
  email: {
    title: 'Email me',
    description: 'Task alerts sent to your CRM profile email.',
  },
  automatic: {
    title: 'Always on (can’t turn off here)',
    inApp: 'Notification bell',
    teamSlack: 'Team #CRM channel',
  },
} as const;

/** Badges for one task/ops alert row — reflects email toggle and row on/off. */
export function getEventDeliveryBadges(
  prefs: NotificationPrefs,
  variant: NotificationAudience,
  eventKey: TaskEventPrefKey,
  enabled: boolean,
): DeliveryBadge[] {
  if (!enabled) {
    return [{ kind: 'off', label: 'Off' }];
  }

  const delivery = EVENT_DELIVERY[eventKey];
  const badges: DeliveryBadge[] = [];

  if (variant === 'portal' || delivery.bell) {
    badges.push({ kind: 'bell', label: 'Bell' });
  }

  if (delivery.email && prefs.email) {
    badges.push({ kind: 'email', label: 'Email' });
  }

  if (badges.length === 0) {
    return [{ kind: 'off', label: 'None' }];
  }

  return badges;
}
